import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faInfoCircle, faPlus } from '@fortawesome/free-solid-svg-icons';

interface AddFirmwareModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (version: string, type: string, internalOnly: boolean, releaseNotes: string, technicalDetails: string, clearState: boolean, targetMobileAppVersion: string, label: string) => void;
  firmwareFiles: Array<{ name: string; path: string }>;
  model: string;
  existingReleases: Array<{ version: string; type: string }>;
}

const AddFirmwareModal: React.FC<AddFirmwareModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  firmwareFiles,
  model,
  existingReleases
}) => {
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('dev');
  const [label, setLabel] = useState<string>('');
  const [internalOnly, setInternalOnly] = useState<boolean>(false);
  const [releaseNotes, setReleaseNotes] = useState<string>('');
  const [technicalDetails, setTechnicalDetails] = useState<string>('');
  const [clearState, setClearState] = useState<boolean>(true);
  const [targetMobileAppVersion, setTargetMobileAppVersion] = useState<string>('');

  if (!isOpen) return null;

  const availableVersions = firmwareFiles
    .filter(file => file.name.startsWith(model.replace(/\s+/g, '_') + '-v'))
    .map(file => {
      const match = file.name.match(/-v([\d.]+)\.bin$/);
      return match ? match[1] : null;
    })
    .filter((version): version is string => version !== null);

  const existingVersions = new Set(existingReleases.map(release => release.version));

  const showLabelField = selectedType === 'dev' || selectedType === 'alpha';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedVersion && selectedType) {
      onAdd(
        selectedVersion,
        selectedType,
        internalOnly,
        releaseNotes,
        technicalDetails,
        clearState,
        targetMobileAppVersion,
        showLabelField ? label.trim() : ''
      );
      onClose();
    }
  };

  const handleInternalOnlyChange = (checked: boolean) => {
    setInternalOnly(checked);
    // Internal-only builds live on an internal track (dev/alpha). Only coerce
    // when the current selection is a public track (beta/prod).
    if (checked && !['dev', 'alpha'].includes(selectedType)) {
      setSelectedType('dev');
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Release</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <small>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2 fa-lg" />
                    A firmware binary must first be uploaded before creating a release
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="version" className="form-label">Firmware Version</label>
                  <select
                    id="version"
                    className="form-select"
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value)}
                    required
                  >
                    <option value="">Select a version...</option>
                    {availableVersions.map((version) => {
                      const isExisting = existingVersions.has(version);
                      return (
                        <option 
                          key={version} 
                          value={version}
                          disabled={isExisting}
                          className={isExisting ? 'text-muted' : ''}
                        >
                          {version} {isExisting ? '(Already Released)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="type" className="form-label">Release Type</label>
                  <select
                    id="type"
                    className="form-select"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    required
                  >
                    <option value="dev">Development (dev)</option>
                    <option value="alpha">Alpha Testing (alpha)</option>
                    {!internalOnly && (
                      <>
                        <option value="beta">Beta Testing (beta)</option>
                        <option value="prod">Production (prod)</option>
                      </>
                    )}
                  </select>
                </div>

                {showLabelField && (
                  <div className="mb-3">
                    <label htmlFor="label" className="form-label">Label (Optional)</label>
                    <input
                      id="label"
                      type="text"
                      className="form-control"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="e.g., hotfix, demo unit"
                    />
                    <div className="form-text">
                      A short label shown next to the version for dev/alpha builds. Hidden once promoted to beta or production.
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="internalOnly"
                      checked={internalOnly}
                      onChange={(e) => handleInternalOnlyChange(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="internalOnly">
                      Internal Only (restricted to dev/alpha types)
                    </label>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Options</label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="clearState"
                        checked={clearState}
                        onChange={(e) => setClearState(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="clearState">
                        Clear State
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="targetMobileAppVersion" className="form-label">Target Mobile App Version (Optional)</label>
                  <input
                    id="targetMobileAppVersion"
                    type="text"
                    className="form-control"
                    value={targetMobileAppVersion}
                    onChange={(e) => setTargetMobileAppVersion(e.target.value)}
                    placeholder="e.g., 2.1.0, 3.0.0"
                  />
                  <div className="form-text">
                    Specify the intended mobile app version for this firmware release.
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="releaseNotes" className="form-label">Release Notes (Optional)</label>
                  <textarea
                    id="releaseNotes"
                    className="form-control"
                    rows={4}
                    value={releaseNotes}
                    onChange={(e) => setReleaseNotes(e.target.value)}
                    placeholder="Enter release notes in markdown format..."
                    style={{ fontFamily: 'monospace' }}
                  />
                  <div className="form-text">
                    Customer-facing notes for the public releases page.
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="technicalDetails" className="form-label">Technical Details (Optional)</label>
                  <textarea
                    id="technicalDetails"
                    className="form-control"
                    rows={4}
                    value={technicalDetails}
                    onChange={(e) => setTechnicalDetails(e.target.value)}
                    placeholder="Enter detailed technical changes, fixes, known issues, checksums, migration notes, etc..."
                    style={{ fontFamily: 'monospace' }}
                  />
                  <div className="form-text">
                    More complete details shown behind a dropdown on the public releases page.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onClose}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={!selectedVersion || !selectedType}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Create Release
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddFirmwareModal;